import { formatISO } from 'date-fns/formatISO';
import {
  AccessibilityBoardingAlighting,
  Leg,
  Notice,
  PTRideLeg,
  SituationCauseEnum,
} from '../../../api/clients/journey-service';
import { InterfaceCusHimMessages } from '../../../components/paf-result-timetable-leg/paf-result-timetable-leg.custom';
import { InterfaceI18nConfig } from '../i18n/i18n.interface';

export const getRideLegs = (legs: Leg[]): PTRideLeg[] => legs.filter((leg) => leg.type === 'PTRideLeg') as PTRideLeg[];

export const getDepartureTimeForLeg = (leg: PTRideLeg): string => {
  const departureTime = leg.serviceJourney.stopPoints[0].departure?.timeAimed;

  return formatISO(departureTime);
};

export const getArrivalTimeTimeForLeg = (leg: PTRideLeg): string => {
  const points = leg.serviceJourney.stopPoints;

  const arrivalTime = points[points.length - 1].arrival?.timeAimed;

  return formatISO(arrivalTime);
};

export const getDeparturePlatformForLeg = (leg: PTRideLeg): string => {
  const quay = leg.serviceJourney.stopPoints[0].departure?.quayFormatted;

  if (quay === undefined) {
    return '';
  }

  return `${leg.serviceJourney.quayTypeShortName} ${quay}`;
};

export const getArrivalPlatformForLeg = (leg: PTRideLeg): string => {
  const points = leg.serviceJourney.stopPoints;
  const lastPoint = points[points.length - 1];
  const quay = lastPoint.arrival?.quayFormatted;

  if (quay === undefined) {
    return '';
  }

  return `${leg.serviceJourney.quayTypeShortName} ${quay}`;
};

export const occupancyStrings = [
  'UNKNOWN',
  'LOW',
  'MEDIUM',
  'HIGH',
];

export const getHighestOccupancyForLeg = (leg: PTRideLeg, classKey: string): string => {
  let index = 0;

  leg.serviceJourney.stopPoints.forEach((point) => {
    const currentIndex = occupancyStrings.indexOf(point[classKey]);

    if (currentIndex > index) {
      index = currentIndex;
    }
  });

  return occupancyStrings[index];
};

export const getPictoForLeg = (leg: PTRideLeg): string => leg.serviceJourney.serviceProducts[0].vehicleMode.corporateIdentityIcon;

export const getDirectionForLeg = (leg: PTRideLeg, config: InterfaceI18nConfig, language: string): string => {
  const {
    directions,
  } = leg.serviceJourney;

  if (directions && directions.length > 0) {
    return `${config.results.rows.direction[language]} ${leg.serviceJourney.directions[0]?.name}`;
  }

  return '';
};

export const getProductIconForLeg = (leg: PTRideLeg): string => leg.serviceJourney.serviceProducts[0].corporateIdentityIcon;

export const getProductTextForLeg = (leg: PTRideLeg): string => leg.serviceJourney.serviceProducts[0].nameFormatted;

export const getProductTextForLegOptional = (leg: PTRideLeg): string => (getProductIconForLeg(leg) === undefined
  ? getProductTextForLeg(leg)
  : '');

export const getTransportationNameForLeg = (leg: PTRideLeg): string => leg.serviceJourney.serviceProducts[0].vehicleMode.name;

export const getOccupancyStringForOccupancies = (
  firstClass: string,
  secondClass: string,
  config: InterfaceI18nConfig,
  language: string,
): string => {
  const templateTexts = config.results.hiddenRowSummary;
  let occupancyInfo = '';

  if (firstClass !== 'UNKNOWN') {
    occupancyInfo += `${templateTexts.occupancy.firstClass[language]} ${templateTexts.occupancy[firstClass][language]}`;
  }

  if (secondClass !== 'UNKNOWN') {
    if (firstClass !== 'UNKNOWN') {
      occupancyInfo += ', ';
    }
    occupancyInfo += `${templateTexts.occupancy.secondClass[language]} ${templateTexts.occupancy[secondClass][language]}`;
  }

  return occupancyInfo;
};

export const getSituationsForLeg = (leg: PTRideLeg): InterfaceCusHimMessages[] => {
  const messages: InterfaceCusHimMessages[] = [];

  leg.serviceJourney.situations.forEach((situation) => {
    const situationCause: SituationCauseEnum = situation.cause as SituationCauseEnum;
    let icon;

    if (situationCause === 'INFORMATION') {
      icon = 'info';
    } else if (situationCause === 'CONSTRUCTION_SITE') {
      icon = 'construction';
    } else if (situationCause === 'DISTURBANCE') {
      icon = 'disruption';
    } else if (situationCause === 'DELAY') {
      icon = 'delay';
    } else if (situationCause === 'TRAIN_REPLACEMENT_BY_BUS') {
      icon = 'replacementbus';
    } else {
      icon = 'info';
    }

    let messageAlreadyThere = false;

    const broadcastMessages = [];

    situation.broadcastMessages.forEach((broadcastMessage) => {
      const {
        title,
      } = broadcastMessage;
      const {
        detail,
      } = broadcastMessage;

      if ((title === detail) || detail.startsWith(title)) {
        broadcastMessages.push(title);
      } else {
        broadcastMessages.push(`${title}: ${detail}`);
      }

    });

    const broadcastMessagesConcat = broadcastMessages.join(', ');

    messages.forEach((messageItem) => {
      if (messageItem.description === broadcastMessagesConcat) {
        messageAlreadyThere = true;
      }
    });

    if (!messageAlreadyThere) {
      messages.push({
        description: broadcastMessagesConcat,
        iconName: icon,
      });
    }
  });

  return messages;
};

export const getCusHimMessagesForLeg = (leg: PTRideLeg): InterfaceCusHimMessages[] => {
  let messages: InterfaceCusHimMessages[] = [];

  // compound train change
  const compoundChange = leg.trainStopAssignmentHint?.compoundTrainChangesTitle;

  if (compoundChange) {
    messages.push({
      description: compoundChange,
      iconName: 'info',
    });
  }

  // situations
  messages = messages.concat(getSituationsForLeg(leg));

  // service alterations
  const alterations = leg.serviceJourney.serviceAlteration;

  if (alterations.partiallyCancelled) {
    messages.push({
      description: alterations.partiallyCancelledFormatted,
      iconName: 'cancellation',
    });
  }

  if (alterations.quayChangedFormatted) {
    messages.push({
      description: alterations.quayChangedFormatted,
      iconName: 'platform-change',
    });
  }

  if (alterations.redirectedFormatted) {
    messages.push({
      description: alterations.redirectedFormatted,
      iconName: 'reroute',
    });
  }

  if (alterations.unplannedStopPointsFormatted) {
    messages.push({
      description: alterations.unplannedStopPointsFormatted,
      iconName: 'add-stop',
    });
  }

  if (!alterations.reachable && alterations.reachableFormatted) {
    messages.push({
      description: alterations.reachableFormatted,
      iconName: 'missed-connection',
    });
  }

  if (alterations.delayFormatted) {
    messages.push({
      description: alterations.delayFormatted,
      iconName: 'delay',
    });
  }

  return messages;
};

// reservation notification
export const generateNotificationDataForLeg = (leg: PTRideLeg): Notice[] => {

  // with connection 9, `hi` and `h1` deliver the exact same template
  const deupedNotices: Notice[] = [];

  const {
    notices,
  } = leg.serviceJourney;

  const relevantNotices = notices.filter((notice) => {
    const relevantNotice = notice.name === 'hi' || notice.name === 'h1';

    return relevantNotice && notice.type === 'INFO';
  });

  relevantNotices.forEach((notice) => {
    let noticeAlreadyThere = false;

    deupedNotices.forEach((dedeupedNotice) => {
      if (notice.text.template === dedeupedNotice.text.template) {
        noticeAlreadyThere = true;
      }
    });

    if (!noticeAlreadyThere) {
      deupedNotices.push(notice);
    }
  });

  return deupedNotices;
};

// wheelchair icon
export const getA11yItemFromLegs = (legs: PTRideLeg[]): AccessibilityBoardingAlighting => {

  const icons: AccessibilityBoardingAlighting[] = [];

  legs.forEach((leg) => {
    const stops = leg.serviceJourney.stopPoints;
    const [departure] = stops;
    const arrival = stops[stops.length - 1];

    icons.push(departure.accessibilityBoardingAlighting);
    icons.push(arrival.accessibilityBoardingAlighting);
  });

  // get icon with highest priority
  let currentPriority = 0;
  let relevantIcon: AccessibilityBoardingAlighting;

  const limitationPriority = [
    'NO_HINT',
    'BOARDING_ALIGHTING_SELF',
    'BOARDING_ALIGHTING_BY_CREW',
    'BOARDING_ALIGHTING_BY_NOTIFICATION',
    'BOARDING_ALIGHTING_SHUTTLE_TRANSPORT',
    'BOARDING_ALIGHTING_NOT_POSSIBLE',
  ];

  icons.forEach((icon) => {
    if (icon) {
      const priorityIndex = limitationPriority.indexOf(icon.limitation);

      if (priorityIndex > currentPriority) {
        currentPriority = priorityIndex;
        relevantIcon = icon;
      }
    }

  });

  return relevantIcon;
};
